/* eslint-disable @typescript-eslint/camelcase */
import { useEffect, useState } from 'react';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { useModal } from '../contexts/ModalContext';
import { FreemiumAd, FreemiumBundleAd } from '../components/FreemiumAd';
import { Ad, AdIdentifier } from '../components/FreemiumAd/types';
import { AdSegmentData } from '../utils/segment-utils';
import { calculateTimeLeft, TimeLeft } from '../utils/date-utils';
import {
  CTAButtonColor,
  CTACopy,
  DismissCopy01,
  DismissCopy02,
  getAds,
} from '../components/FreemiumAd/AdConfig';
import useWindowWidth from './useWindowWidth';
import { EventNames, trackPageEvents, trackEvent } from '../lib/analytics';

type withoutVariant = (ad: Ad, isMobile: boolean) => AdSegmentData;
export type ShowAd = (
  partialSegmentData: withoutVariant,
  adVariant?: string,
  forceShowAd?: boolean,
) => void;

const getAdIndex = (ads: Ad[], adVariant?: string): number => {
  if (adVariant) {
    return +adVariant;
  }
  return Math.floor(Math.random() * ads.length);
};

interface UseAd {
  adFrequencyPercentage?: number; // 100 =  show 100% of time, 50 = show 50% of time
  isLoading?: boolean;
}

const useAd = ({ adFrequencyPercentage = 100, isLoading }: UseAd = {}): { showAd: ShowAd } => {
  const { isFreeUser, hasCheckedSession, subscriptionExpirationDate, user } = usePlayUser();
  const { showModal, hideModal, clearModals } = useModal();
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0 });

  useEffect(() => {
    windowWidth && setIsMobile(windowWidth < 1280);
  }, [windowWidth]);

  useEffect(() => {
    if (subscriptionExpirationDate) {
      setTimeLeft(calculateTimeLeft(subscriptionExpirationDate));
    }
  }, [subscriptionExpirationDate]);

  const showAd: ShowAd = (lessonSegmentData, adVariant, forceShowAd = false): void => {
    const probabilityOfAd = adFrequencyPercentage / 100;
    const randomNumber = Math.random();
    const shouldSeeAd =
      forceShowAd ||
      (!isLoading &&
        isFreeUser &&
        hasCheckedSession &&
        isMobile !== undefined &&
        randomNumber < probabilityOfAd);
    const ads = getAds(timeLeft);
    const adIndex = getAdIndex(ads, adVariant);
    const segmentData = lessonSegmentData(ads[adIndex], isMobile as boolean);
    const segmentAdCopy = ads[adIndex].adCopy.join(' ');

    const trackingProperties = {
      fc_id: user?.userID,
      ad_bodycopy: segmentAdCopy,
      cta_buttoncolor: CTAButtonColor,
      ad_headlinecopy: segmentAdCopy,
      ...segmentData,
    };

    const eventProperties = {
      test_drive_days_remaining: timeLeft.days,
      test_drive_hours_remaining: timeLeft.hours,
    };

    const onDismiss = (): void => {
      hideModal();

      trackEvent(EventNames.SUBSCRIPTION_AD_MODAL_DECLINED, {
        ...trackingProperties,
        ...eventProperties,
        cta_buttoncopy:
          ads[adIndex].adIdentifier === AdIdentifier.MUSTANG_MICRO_BUNDLE
            ? DismissCopy02
            : DismissCopy01,
      });
    };

    const onUpgrade = (): void => {
      hideModal();
      clearModals();

      trackEvent(EventNames.SUBSCRIPTION_AD_MODAL_ACCEPTED, {
        ...trackingProperties,
        ...eventProperties,
        cta_buttonCopy: CTACopy,
      });
    };

    if (shouldSeeAd) {
      showModal(
        ads[adIndex].adIdentifier === AdIdentifier.MUSTANG_MICRO_BUNDLE
          ? FreemiumBundleAd
          : FreemiumAd,
        {
          onDismiss,
          onUpgrade,
          isMobile,
          ad: ads[adIndex],
          showLargeHeadline:
            ads[adIndex].adIdentifier === AdIdentifier.GEAR_DISCOUNT || timeLeft.hours <= 0,
        },
      );
      trackPageEvents(
        EventNames.SUBSCRIPTION_AD_MODAL,
        {
          ...trackingProperties,
          cta_buttoncopy: CTACopy,
        },
        {
          course_title: segmentData?.['course_name'],
          lesson_title: segmentData?.['lesson_name'],
        },
      );
    }
  };
  return { showAd };
};

export default useAd;
